import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { buttonVariants } from '../../components/ui/button';
import { cn } from '@folio/lib';
import {
  useFeatureFlag,
  useKorintConfig,
  useKorintProductConfig,
  type KorintModulesConfig,
} from 'korint-sdk/react';
import { FileTextIcon, MailIcon, PhoneIcon, PencilIcon } from 'lucide-react';
import { usePolicyOnDashboard } from '../../utils/usePolicyOnDashboard';
import { Group, Stack } from '@folio/components/ui/flex';
import { useTranslation } from 'react-i18next';
import { generatePath, Link } from 'react-router-dom';

type ContactType = NonNullable<
  NonNullable<KorintModulesConfig['policyholderClaimsDeclarationModuleConfig']>['contacts']
>[number]['type'];

function ContactIcon({ type }: { type: ContactType }) {
  if (type === 'email') {
    return <MailIcon className="h-4 w-4 mr-2" />;
  }
  if (type === 'phone') {
    return <PhoneIcon className="h-4 w-4 mr-2" />;
  }
  if (type === 'mail') {
    return <PencilIcon className="h-4 w-4 mr-2" />;
  }
  return <FileTextIcon className="h-4 w-4 mr-2" />;
}

function ContactLink({ type, value }: { type: ContactType; value: string }) {
  if (type === 'email') {
    return (
      <a href={`mailto:${value}`} className="underline">
        {value}
      </a>
    );
  }
  if (type === 'phone') {
    return (
      <a href={`tel:${value}`} className="underline">
        {value}
      </a>
    );
  }
  return <span className="whitespace-pre-line block">{value}</span>;
}

function ContactLabel({ label }: { label?: string }) {
  if (!label) return null;

  return <b className="font-bold">{label}&nbsp;:&nbsp;</b>;
}

export function PolicyholderClaimsDeclarationModule() {
  const { t } = useTranslation();
  const { korintConfig } = useKorintConfig();
  const { policy } = usePolicyOnDashboard();
  const { productsConfig } = useKorintProductConfig();
  const productConfig = productsConfig[policy.productId]?.policyholderClaimsDeclarationModuleConfig;
  const config =
    productConfig ?? korintConfig.modulesConfig?.policyholderClaimsDeclarationModuleConfig;
  const featureFlagEnabled = useFeatureFlag(config?.featureFlag, policy.productId);

  if (!config || (config.featureFlag && !featureFlagEnabled)) {
    return null;
  }

  const { title, description, cta, contacts } = config;
  const translationPrefix = `products.${policy.productId}.claimsDeclaration`;
  const ctaLabel = cta && t(`${translationPrefix}.cta`, { defaultValue: cta.label });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(`${translationPrefix}.title`, { defaultValue: title })}</CardTitle>
      </CardHeader>
      <Stack asChild>
        <CardContent>
          {description && (
            <p className="whitespace-pre-line">
              {t(`${translationPrefix}.description`, { defaultValue: description })}
            </p>
          )}
          {cta?.url.startsWith('/') && (
            <Link
              to={generatePath(cta.url, { policyId: policy.id })}
              className={cn(buttonVariants(), 'self-start')}
            >
              {ctaLabel}
            </Link>
          )}
          {cta && !cta.url.startsWith('/') && (
            <a
              href={cta.url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(buttonVariants(), 'self-start')}
            >
              {ctaLabel}
            </a>
          )}
          {contacts?.map((contact, index) => {
            return (
              <div key={`${contact.value}-${index}`}>
                <Group gap={0}>
                  {contact.showIcon && <ContactIcon type={contact.type} />}
                  <span className="">
                    <ContactLabel label={contact.label} />
                    <span>{contact.prefix}</span>
                    <ContactLink type={contact.type} value={contact.value} />
                  </span>
                </Group>
                {contact.extraInfo && (
                  <div className="text-gray-600 font-light text-sm ">{contact.extraInfo}</div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Stack>
    </Card>
  );
}
